import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AdminCard, AdminPageHeader } from "@/components/admin/admin-shell";
import { fetchAllMessages, type AdminMessage } from "@/lib/messages";

export const Route = createFileRoute("/_authenticated/admin/mesajlar")({
  component: Page,
});

type Thread = {
  key: string;
  a: string;
  b: string;
  messages: AdminMessage[];
};

function Page() {
  const [search, setSearch] = useState("");
  const [openKey, setOpenKey] = useState<string | null>(null);

  const q = useQuery({ queryKey: ["admin", "messages"], queryFn: () => fetchAllMessages() });

  const threads = useMemo<Thread[]>(() => {
    const map = new Map<string, Thread>();
    for (const m of q.data ?? []) {
      const [x, y] = [m.sender_id, m.recipient_id].sort() as [string, string];
      const key = `${x}|${y}`;
      const t = map.get(key);
      if (t) t.messages.push(m);
      else
        map.set(key, {
          key,
          a: m.sender_id === x ? m.senderName : m.recipientName,
          b: m.sender_id === x ? m.recipientName : m.senderName,
          messages: [m],
        });
    }
    return [...map.values()];
  }, [q.data]);

  const filtered = threads.filter((t) =>
    `${t.a} ${t.b} ${t.messages.map((m) => m.body).join(" ")}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <div>
      <AdminPageHeader
        title="Mesajlar"
        description="İstifadəçilər arasındakı yazışmalara nəzarət"
      />
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="İstifadəçi adı və ya mesaj mətni üzrə axtar"
        className="mb-4 h-11 rounded-full"
      />

      {q.isLoading && <p className="text-sm text-muted-foreground">Yüklənir…</p>}

      {!q.isLoading && filtered.length === 0 && (
        <AdminCard>
          <p className="p-10 text-center text-sm text-muted-foreground">Yazışma tapılmadı.</p>
        </AdminCard>
      )}

      <div className="grid gap-3">
        {filtered.map((t) => {
          const last = t.messages[0]!;
          const open = openKey === t.key;
          return (
            <AdminCard key={t.key}>
              <button
                onClick={() => setOpenKey(open ? null : t.key)}
                className="flex w-full items-center gap-3 p-4 text-left"
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                  <MessageSquare className="size-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold">
                    <span className="text-primary">{last.senderName}</span>
                    <span className="mx-1 text-muted-foreground">→</span>
                    <span>{last.recipientName}</span>
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">{last.body}</span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {t.messages.length} mesaj
                </span>
              </button>

              {open && (
                <div className="space-y-2 border-t border-border p-4">
                  {[...t.messages]
                    .sort((x, y) => x.created_at.localeCompare(y.created_at))
                    .map((m) => (
                      <div key={m.id} className="rounded-2xl border border-border bg-background p-3">
                        <p className="text-xs font-bold text-primary">
                          {m.senderName} → {m.recipientName}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm">{m.body}</p>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {new Date(m.created_at).toLocaleString("az-AZ")}
                        </p>
                      </div>
                    ))}
                </div>
              )}
            </AdminCard>
          );
        })}
      </div>
    </div>
  );
}